/**
 * SocialCast browser client for the server-side Apteva SDK adapter.
 *
 * Calls the server-side /api/ proxy routes.
 * API key stays on the server — never exposed to the browser.
 * Auth tokens are stored in httpOnly cookies managed by the server.
 */

interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean>;
}

async function apiRequest<T = unknown>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { params, headers, ...fetchOptions } = options;

  let url = `/api${endpoint}`;
  if (params) {
    const searchParams = new URLSearchParams(
      Object.entries(params).map(([key, value]) => [key, String(value)])
    );
    url += `?${searchParams.toString()}`;
  }

  const response = await fetch(url, {
    ...fetchOptions,
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: "Unknown error" }));
    throw new Error((errorData as any).error || `API request failed: ${response.status}`);
  }

  return response.json() as Promise<T>;
}

/** Generic data API */
export const api = {
  get: <T = unknown>(endpoint: string, params?: Record<string, string | number | boolean>) =>
    apiRequest<T>(endpoint, { method: "GET", params }),

  post: <T = unknown>(endpoint: string, data?: unknown) =>
    apiRequest<T>(endpoint, { method: "POST", body: JSON.stringify(data) }),

  put: <T = unknown>(endpoint: string, data?: unknown) =>
    apiRequest<T>(endpoint, { method: "PUT", body: JSON.stringify(data) }),

  delete: <T = unknown>(endpoint: string) =>
    apiRequest<T>(endpoint, { method: "DELETE" }),
};

/** Auth API */
export const auth = {
  login: (email: string, password: string, rememberMe = false) =>
    apiRequest<{ success: boolean; user: any }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password, rememberMe }),
    }),

  register: (data: { username: string; email: string; password: string; display_name?: string }) =>
    apiRequest<{ success: boolean; user?: any; autoLogin: boolean; verification_required?: boolean; verification_email_enabled?: boolean }>("/auth/register", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  forgotPassword: (email: string) =>
    apiRequest<{ success: boolean }>("/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),

  resetPassword: (token: string, password: string) =>
    apiRequest<{ success: boolean }>("/auth/reset-password", {
      method: "POST",
      body: JSON.stringify({ token, password }),
    }),

  logout: () =>
    apiRequest<{ success: boolean }>("/auth/logout", { method: "POST" }),

  me: () =>
    apiRequest<{ success: boolean; user: any }>("/auth/me", { method: "GET" }),
};
